/**
 * Post Performance Ledger — pure capture of what a published post ACTUALLY
 * did, so the Content Engine can learn what works for THIS author instead
 * of only grading drafts against generic best practices.
 *
 * Fixes the top auditor findings: the engine scored drafts but never
 * captured real-world performance, so no per-author signal could ever be
 * derived. This module is the missing capture half — a `LoggedPost` is one
 * published post plus its observed metrics, with the post's FEATURES
 * (hook style, topic, word count) derived deterministically from the text
 * by exported pure extractors so the same text always yields the same
 * features and tests stay green.
 *
 * PURE by contract: no store, no firebase, no network. Records live in
 * memory and are passed in by callers; persistence is the caller's concern.
 * `toPerformanceProvenanceEntry` reuses the shipped provenance builder from
 * `lib/agent/provenance` (same evidence/confidence/digest shape the Decision
 * Ledger seals) but performs NO ledger writes.
 */

import {
  buildProvenance,
  deepLinkFor,
  provenanceDigest,
  type ProposalProvenance,
} from '../agent/provenance';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Observed metrics for one published post. All honest raw counts. */
export type PostMetrics = {
  views: number;
  likes: number;
  replies: number;
  bookmarks: number;
  /** Followers gained (or lost, negative) attributable to the post window. */
  followerDelta: number;
};

/** One published post with its derived features + observed metrics. */
export type LoggedPost = {
  id: string;
  text: string;
  /** Format id the post was published as (e.g. 'linkedinPost', 'xThread'). */
  format: string;
  /** Derived by {@link extractHookStyle} — never hand-entered. */
  hookStyle: string;
  /** Derived by {@link extractTopic} — never hand-entered. */
  topic: string;
  /** Derived by {@link countWords} — never hand-entered. */
  wordCount: number;
  /** Local hour (0–23) the post went live. */
  postedAtHour: number;
  metrics: PostMetrics;
  /** Epoch ms the performance was logged. */
  loggedAt: number;
};

// ---------------------------------------------------------------------------
// Pure feature extractors
// ---------------------------------------------------------------------------

/** Warm-up openers (mirrors the shipped scorer's scroll-past lexicon). */
const WARMUP_OPENERS: RegExp[] = [
  /^some thoughts on/i,
  /^in today['’]?s/i,
  /^excited to/i,
  /^thrilled to/i,
  /^proud to/i,
  /^happy to (announce|share)/i,
  /^i['’]?m (excited|thrilled|proud|pleased)/i,
  /^we['’]?re (excited|thrilled|proud|pleased)/i,
  /^pleased to announce/i,
  /^check out/i,
  /^just wanted to/i,
];

/** Scene/person openers — the field study's strongest first-line pattern. */
const SCENE_OPENER =
  /^(met |i |i['’]m |we |she |he |they |last |yesterday|this morning|today)/i;

/** Words too generic to be a topic, even when frequent. */
const TOPIC_STOPWORDS = new Set([
  'this', 'that', 'these', 'those', 'with', 'from', 'have', 'having', 'your',
  'yours', 'what', 'when', 'where', 'which', 'while', 'will', 'would', 'could',
  'should', 'about', 'after', 'before', 'because', 'been', 'being', 'were',
  'they', 'them', 'their', 'there', 'then', 'than', 'just', 'like', 'more',
  'most', 'much', 'many', 'some', 'every', 'each', 'other', 'into', 'over',
  'only', 'also', 'very', 'here', 'does', 'doing', 'done', 'dont', 'cant',
  'want', 'need', 'needs', 'make', 'makes', 'gets', 'goes', 'going', 'know',
  'thing', 'things', 'people', 'really', 'still', 'even', 'ever', 'never',
  'back', 'down', 'first', 'last', 'next', 'well', 'good', 'time', 'today',
  'yesterday', 'week', 'year', 'years',
]);

/** Whitespace-delimited word count. Empty/blank text is honestly 0. */
export function countWords(text: string): number {
  return (text ?? '').split(/\s+/).filter((w) => w.length > 0).length;
}

/**
 * Classify the opening hook of a post into a small deterministic vocabulary:
 * 'question' | 'number-led' | 'scene-led' | 'warm-up' | 'statement' | 'none'.
 * Only the first non-empty line is judged — that is the hook the scroller sees.
 */
export function extractHookStyle(text: string): string {
  const firstLine = (text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!firstLine) return 'none';
  if (WARMUP_OPENERS.some((re) => re.test(firstLine))) return 'warm-up';
  if (/\?\s*$/.test(firstLine)) return 'question';
  if (/[\d$%]/.test(firstLine)) return 'number-led';
  if (SCENE_OPENER.test(firstLine)) return 'scene-led';
  return 'statement';
}

/**
 * Deterministic topic keyword: the most frequent substantive word (4+
 * letters, not a stopword), lowercased. Ties break to the word that appears
 * EARLIEST in the text so the result is stable. 'general' when nothing
 * substantive exists — never a fabricated topic.
 */
export function extractTopic(text: string): string {
  const words = (text ?? '')
    .toLowerCase()
    .split(/[^a-z0-9'-]+/)
    .map((w) => w.replace(/^['-]+|['-]+$/g, ''))
    .filter((w) => w.length >= 4 && !/^\d+$/.test(w) && !TOPIC_STOPWORDS.has(w));
  if (words.length === 0) return 'general';

  const counts = new Map<string, { count: number; firstIndex: number }>();
  words.forEach((w, i) => {
    const cur = counts.get(w);
    if (cur) cur.count += 1;
    else counts.set(w, { count: 1, firstIndex: i });
  });

  let best = '';
  let bestCount = 0;
  let bestFirst = Number.MAX_SAFE_INTEGER;
  for (const [w, { count, firstIndex }] of counts) {
    if (count > bestCount || (count === bestCount && firstIndex < bestFirst)) {
      best = w;
      bestCount = count;
      bestFirst = firstIndex;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/**
 * Build a {@link LoggedPost} from raw input: features (hookStyle / topic /
 * wordCount) are DERIVED from the text via the pure extractors above —
 * never hand-entered — and the metrics object is defensively copied so the
 * record can't be mutated from outside.
 */
export function createLoggedPost(input: {
  id: string;
  text: string;
  format: string;
  postedAtHour: number;
  metrics: PostMetrics;
}): LoggedPost {
  return {
    id: input.id,
    text: input.text,
    format: input.format,
    hookStyle: extractHookStyle(input.text),
    topic: extractTopic(input.text),
    wordCount: countWords(input.text),
    postedAtHour: input.postedAtHour,
    metrics: { ...input.metrics },
    loggedAt: Date.now(),
  };
}

/**
 * Engagement rate = (likes + replies + bookmarks) / views. Honest 0 when
 * there are no views — an unseen post has no measurable engagement, and we
 * never divide by zero into a fabricated infinity.
 */
export function engagementRate(m: PostMetrics): number {
  if (!Number.isFinite(m.views) || m.views <= 0) return 0;
  return (m.likes + m.replies + m.bookmarks) / m.views;
}

/**
 * Express a logged post's performance as a provenance-shaped record —
 * the SAME evidence/confidence/digest shape the Decision Ledger seals
 * (built with the shipped `buildProvenance` / `provenanceDigest`, never a
 * reimplementation). Confidence is sample-size honest: it scales with the
 * post's view count (1000+ views ≈ full confidence in the observed rate)
 * so a 12-view post never claims a strong signal. NO ledger writes happen
 * here — the caller decides whether/where to seal it.
 */
export function toPerformanceProvenanceEntry(
  post: LoggedPost,
): ProposalProvenance & { digest: string } {
  const rate = engagementRate(post.metrics);
  const provenance = buildProvenance({
    evidence: [
      {
        kind: 'deliverable',
        id: post.id,
        label: `${post.format} post · ${post.metrics.views} views · ${post.hookStyle} hook`,
        deepLink: deepLinkFor('deliverable', post.id),
      },
    ],
    confidence: {
      score: Math.min(1, post.metrics.views / 1000),
      basis:
        `Observed performance of logged post ${post.id}: ${post.metrics.views} views, ` +
        `${post.metrics.likes} likes, ${post.metrics.replies} replies, ` +
        `${post.metrics.bookmarks} bookmarks (engagement rate ${(rate * 100).toFixed(2)}%, ` +
        `follower delta ${post.metrics.followerDelta}).`,
    },
  });
  return { ...provenance, digest: provenanceDigest(provenance) };
}
